"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { Project, ProjectMember } from "@audra-flow/types"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

interface CreateProjectInput {
  name: string
  type: "enterprise" | "startup" | "saas" | "internal"
  description?: string
  deadline?: string
  emoji?: string
  collaboratorIds?: string[]
}

interface UpdateProjectInput extends Partial<CreateProjectInput> {
  id: string
}

// API response types
interface APIProjectMember {
  id: string
  user: {
    id: string
    name: string | null
    email: string
    avatarUrl: string | null
  }
  role: {
    id: string
    name: string
  }
}

interface APIProject {
  id: string
  name: string
  description: string | null
  type: "ENTERPRISE" | "STARTUP" | "SAAS" | "INTERNAL"
  status: string
  emoji: string | null
  deadline: string | null
  createdAt: string
  updatedAt: string
  members: APIProjectMember[]
  createdBy: {
    id: string
    name: string | null
    email: string
  }
  _count?: {
    goals: number
    personas: number
    journeys: number
    useCases: number
    baseSpecs: number
    contracts: number
    technicalSpecs: number
    stories: number
    evidences: number
    releases: number
    documents: number
    discoveryArtefacts: number
    definitionArtefacts: number
    designArtifacts: number
    deliveryArtifacts: number
  }
}

interface APIPaginatedResponse {
  success: boolean
  data: APIProject[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface APISingleResponse {
  success: boolean
  data: APIProject
  message?: string
}

// Color palette for member avatars
const MEMBER_COLORS = [
  "#2b7fff", "#ad46ff", "#00c950", "#f0b100", "#ff6b6b",
  "#00d4aa", "#ff8c00", "#9b59b6", "#3498db", "#e74c3c"
]

// Transform API project to frontend format
function transformProject(apiProject: APIProject): Project {
  const members: ProjectMember[] = apiProject.members.map((m, index) => {
    const name = m.user.name || m.user.email.split("@")[0]
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
    
    return {
      id: m.user.id,
      name,
      initials,
      color: MEMBER_COLORS[index % MEMBER_COLORS.length],
    }
  })

  // Calculate progress based on 4-stage pipeline completion
  const counts = apiProject._count || {
    goals: 0, personas: 0, journeys: 0, useCases: 0,
    baseSpecs: 0, contracts: 0, technicalSpecs: 0,
    stories: 0, evidences: 0, releases: 0, documents: 0,
    discoveryArtefacts: 0, definitionArtefacts: 0,
    designArtifacts: 0, deliveryArtifacts: 0,
  }
  const stages = [
    counts.goals > 0 || counts.personas > 0 || counts.discoveryArtefacts > 0,                                        // Discovery
    counts.journeys > 0 || counts.useCases > 0 || counts.definitionArtefacts > 0,                                    // Definition
    counts.baseSpecs > 0 || counts.technicalSpecs > 0 || counts.contracts > 0 || counts.designArtifacts > 0,         // Specification
    counts.stories > 0 || counts.releases > 0 || counts.deliveryArtifacts > 0,                                       // Delivery
  ]
  const progress = stages.filter(Boolean).length * 25

  // Build artefacts list with counts
  const artefacts: string[] = []
  if (counts.goals > 0) artefacts.push(`${counts.goals} goal${counts.goals !== 1 ? "s" : ""}`)
  if (counts.personas > 0) artefacts.push(`${counts.personas} persona${counts.personas !== 1 ? "s" : ""}`)
  if (counts.journeys > 0) artefacts.push(`${counts.journeys} journey${counts.journeys !== 1 ? "s" : ""}`)
  if (counts.stories > 0) artefacts.push(`${counts.stories} stor${counts.stories !== 1 ? "ies" : "y"}`)

  return {
    id: apiProject.id,
    name: apiProject.name,
    type: apiProject.type.toLowerCase() as Project["type"],
    description: apiProject.description || undefined,
    emoji: apiProject.emoji || "📁",
    deadline: apiProject.deadline ? apiProject.deadline.split("T")[0] : "",
    progress,
    stages: {
      discovery: stages[0],
      definition: stages[1],
      specification: stages[2],
      delivery: stages[3],
    },
    members,
    artefacts,
  }
}

// Fetch all projects from API
async function fetchProjects(token: string | null): Promise<Project[]> {
  if (!token) {
    console.warn("No auth token, returning empty projects")
    return []
  }

  try {
    const response = await fetch(`${API_BASE}/api/projects`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      // If unauthorized, return empty array (auth will handle redirect)
      if (response.status === 401) {
        console.warn("Unauthorized, returning empty projects")
        return []
      }
      throw new Error(`Failed to fetch projects: ${response.statusText}`)
    }

    const data: APIPaginatedResponse = await response.json()
    
    if (!data.success || !data.data) {
      return []
    }

    return data.data.map(transformProject)
  } catch (error) {
    console.error("Error fetching projects:", error)
    throw error
  }
}

// Fetch single project from API
async function fetchProject(id: string, token: string | null): Promise<Project | null> {
  if (!token) {
    console.warn("No auth token, returning null")
    return null
  }

  try {
    const response = await fetch(`${API_BASE}/api/projects/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to fetch project: ${response.statusText}`)
    }

    const data: APISingleResponse = await response.json()
    
    if (!data.success || !data.data) {
      return null
    }

    return transformProject(data.data)
  } catch (error) {
    console.error("Error fetching project:", error)
    throw error
  }
}

// Create project via API
async function createProject(input: CreateProjectInput, token: string | null): Promise<Project> {
  if (!token) {
    throw new Error("Authentication required")
  }

  // Build request body, omitting empty optional fields
  const requestBody: Record<string, unknown> = {
    name: input.name,
    type: input.type.toUpperCase(),
  }
  
  // Only include optional fields if they have actual values
  if (input.description?.trim()) {
    requestBody.description = input.description.trim()
  }
  if (input.deadline) {
    requestBody.deadline = new Date(input.deadline).toISOString()
  }
  if (input.emoji) {
    requestBody.emoji = input.emoji
  }
  if (input.collaboratorIds && input.collaboratorIds.length > 0) {
    requestBody.collaboratorIds = input.collaboratorIds
  }

  const response = await fetch(`${API_BASE}/api/projects`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to create project")
  }

  const data: APISingleResponse = await response.json()
  return transformProject(data.data)
}

// Update project via API
async function updateProject(input: UpdateProjectInput, token: string | null): Promise<Project> {
  if (!token) {
    throw new Error("Authentication required")
  }

  const { id, ...updates } = input
  
  const body: Record<string, unknown> = {}
  if (updates.name) body.name = updates.name
  if (updates.type) body.type = updates.type.toUpperCase()
  if (updates.description !== undefined) body.description = updates.description
  if (updates.deadline) body.deadline = new Date(updates.deadline).toISOString()
  if (updates.emoji) body.emoji = updates.emoji

  const response = await fetch(`${API_BASE}/api/projects/${id}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to update project")
  }

  const data: APISingleResponse = await response.json()
  return transformProject(data.data)
}

// Delete project via API
async function deleteProject(id: string, token: string | null): Promise<void> {
  if (!token) {
    throw new Error("Authentication required")
  }

  const response = await fetch(`${API_BASE}/api/projects/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to delete project")
  }
}

// React Query hooks

export function useProjects() {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => fetchProjects(token),
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof Error && error.message.includes("401")) {
        return false
      }
      return failureCount < 2
    },
  })
}

export function useProject(id: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", id],
    queryFn: () => fetchProject(id, token),
    enabled: !!id && !!token,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("401")) {
        return false
      }
      return failureCount < 2
    },
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
    },
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (input: UpdateProjectInput) => updateProject(input, token),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["projects", data.id] })
    },
  })
}

export function useDeleteProject() {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (id: string) => deleteProject(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] })
    },
  })
}

// ============================================
// Project Members Hooks
// ============================================

export interface ProjectMemberDetail {
  id: string
  user: {
    id: string
    name: string | null
    email: string
    avatarUrl: string | null
  }
  role: {
    id: string
    name: string
    description: string | null
  }
  joinedAt?: string
}

interface AddMemberInput {
  projectId: string
  userId?: string
  email?: string
  roleId: string
}

interface UpdateMemberInput {
  projectId: string
  memberId: string
  roleId: string
}

interface RemoveMemberInput {
  projectId: string
  memberId: string
}

// Fetch project members
async function fetchProjectMembers(projectId: string, token: string | null): Promise<ProjectMemberDetail[]> {
  if (!token) return []

  const response = await fetch(`${API_BASE}/api/projects/${projectId}/members`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      return []
    }
    throw new Error("Failed to fetch project members")
  }

  const data = await response.json()
  return data.data || []
}

// Add member to project
async function addProjectMember(input: AddMemberInput, token: string | null): Promise<ProjectMemberDetail> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/projects/${input.projectId}/members`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      userId: input.userId,
      email: input.email,
      roleId: input.roleId,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to add member")
  }

  const data = await response.json()
  return data.data
}

// Update member role
async function updateProjectMember(input: UpdateMemberInput, token: string | null): Promise<ProjectMemberDetail> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${input.projectId}/members/${input.memberId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ roleId: input.roleId }),
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to update member")
  }

  const data = await response.json()
  return data.data
}

// Remove member from project
async function removeProjectMember(input: RemoveMemberInput, token: string | null): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${input.projectId}/members/${input.memberId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to remove member")
  }
}

export function useProjectMembers(projectId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["projects", projectId, "members"],
    queryFn: () => fetchProjectMembers(projectId, token),
    enabled: !!projectId && !!token,
  })
}

export function useAddProjectMember() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: AddMemberInput) => addProjectMember(input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId, "members"] })
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] })
    },
  })
}

export function useUpdateProjectMember() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: UpdateMemberInput) => updateProjectMember(input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId, "members"] })
    },
  })
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: RemoveMemberInput) => removeProjectMember(input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId, "members"] })
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] })
    },
  })
}
